import type { AISuggestion } from "@/types/quote";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, X, Check, Loader2, Trash2 } from "lucide-react";

interface AISuggestionPanelProps {
	suggestions: AISuggestion[];
	loading: boolean;
	onDismiss: (index: number) => void;
	onClear: () => void;
	onApplyTerm: (term: string) => void;
	onApplyDescription: (desc: string) => void;
}

const typeConfig: Record<string, { label: string; color: string }> = {
	pricing: { label: "Pricing", color: "text-emerald-400 bg-emerald-500/15" },
	terms: { label: "Terms", color: "text-blue-400 bg-blue-500/15" },
	description: { label: "Description", color: "text-violet-400 bg-violet-500/15" },
	discount: { label: "Discount", color: "text-amber-400 bg-amber-500/15" },
};

export function AISuggestionPanel({
	suggestions,
	loading,
	onDismiss,
	onClear,
	onApplyTerm,
	onApplyDescription,
}: AISuggestionPanelProps) {
	if (suggestions.length === 0 && !loading) {
		return (
			<Card className="border-zinc-700/50 shadow-none border-dashed bg-zinc-800/30">
				<CardContent className="p-6 text-center">
					<div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center mx-auto mb-3">
						<Sparkles className="w-5 h-5 text-violet-400" />
					</div>
					<h3 className="text-sm font-medium text-zinc-300 mb-1">AI Assistant</h3>
					<p className="text-xs text-zinc-500 leading-relaxed">
						Click "Price Check" on any line item or "Suggest Terms" to get intelligent suggestions.
					</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="border-violet-500/30 shadow-none bg-gradient-to-b from-violet-500/10 to-zinc-800/50">
			<CardContent className="p-5">
				<div className="flex items-center justify-between mb-4">
					<div className="flex items-center gap-2">
						<Sparkles className="w-4 h-4 text-violet-400" />
						<h3 className="text-xs font-medium uppercase tracking-wider text-violet-400">
							AI Suggestions
						</h3>
					</div>
					{suggestions.length > 0 && (
						<button
							onClick={onClear}
							className="text-zinc-500 hover:text-zinc-300 transition-colors"
						>
							<Trash2 className="w-3.5 h-3.5" />
						</button>
					)}
				</div>

				{loading && (
					<div className="flex items-center gap-2 text-sm text-violet-400 mb-3">
						<Loader2 className="w-3.5 h-3.5 animate-spin" />
						<span>Analyzing...</span>
					</div>
				)}

				<div className="space-y-3">
					{suggestions.map((suggestion, i) => {
						const config = (typeConfig[suggestion.type] ?? typeConfig.pricing)!;
						return (
							<div
								key={i}
								className="bg-zinc-800/60 border border-zinc-700/40 rounded-lg p-3 group"
							>
								<div className="flex items-start justify-between mb-2">
									<Badge
										variant="secondary"
										className={`text-[10px] font-normal ${config.color}`}
									>
										{config.label}
									</Badge>
									<button
										onClick={() => onDismiss(i)}
										className="text-zinc-600 hover:text-zinc-300 transition-colors opacity-0 group-hover:opacity-100"
									>
										<X className="w-3 h-3" />
									</button>
								</div>
								<p className="text-sm text-zinc-300 mb-1.5 leading-relaxed">
									{suggestion.content}
								</p>
								<p className="text-[11px] text-zinc-500 leading-relaxed mb-2">
									{suggestion.reasoning}
								</p>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-1">
										<div className="h-1 w-8 bg-zinc-700 rounded-full overflow-hidden">
											<div
												className="h-full bg-violet-400 rounded-full"
												style={{ width: `${suggestion.confidence * 100}%` }}
											/>
										</div>
										<span className="text-[10px] text-zinc-500">
											{Math.round(suggestion.confidence * 100)}%
										</span>
									</div>
									{(suggestion.type === "terms" || suggestion.type === "description") && (
										<Button
											variant="ghost"
											size="sm"
											onClick={() => {
												if (suggestion.type === "terms") {
													onApplyTerm(suggestion.content);
												} else {
													onApplyDescription(suggestion.content);
												}
												onDismiss(i);
											}}
											className="h-6 text-[11px] text-violet-400 hover:text-violet-300 hover:bg-violet-500/10"
										>
											<Check className="w-3 h-3 mr-1" />
											Apply
										</Button>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</CardContent>
		</Card>
	);
}
